import type { StructureT } from "@/app/types/structure";
import type { VehicleT } from "@/app/types/gebrauchtwagen";

import {
  VEHICLE_FILTER_DEFINITIONS,
  type FilterOption,
  type FilterOptionContext,
  type VehicleFilterDefinition,
  type VehicleFilterDefinitionById,
  type VehicleFilterFacets,
  type VehicleFilterId,
  type VehicleFilterValues,
} from "./definitions";
import type {
  VehicleColorFilterValue,
  VehicleConditionFilterValue,
  VehicleFuelFilterValue,
  VehicleKilometersFilterValue,
  VehicleLocationFilterValue,
  VehicleMakeFilterValue,
  VehicleModelFilterValue,
  VehiclePowerRangeFilterValue,
  VehiclePriceRangeFilterValue,
  VehicleTransmissionFilterValue,
  VehicleTypeFilterValue,
} from "./value-types";
import { isExcludedMakeLabel } from "./constants";
import { getVehicleMakeId } from "./runtime-utils";

export type VehicleFilterApiInput = Partial<{
  search: string;
  condition: VehicleConditionFilterValue;
  make: VehicleMakeFilterValue;
  model: VehicleModelFilterValue;
  vehicletype: VehicleTypeFilterValue[];
  color: VehicleColorFilterValue[];
  fuel: VehicleFuelFilterValue[];
  transmission: VehicleTransmissionFilterValue[];
  location: VehicleLocationFilterValue[];
  kilometers: VehicleKilometersFilterValue;
  priceRange: VehiclePriceRangeFilterValue;
  powerRange: VehiclePowerRangeFilterValue;
  sort:
    | "price-asc"
    | "price-desc"
    | "date-desc"
    | "kilometers-asc"
    | "kilometers-desc"
    | "name-asc"
    | "name-desc"
    | "power-asc"
    | "power-desc";
}>;

export type ActiveFilterChip = {
  id: VehicleFilterId;
  label: string;
  param: string;
};

const filterDefinitionMap = new Map<VehicleFilterId, VehicleFilterDefinition>(
  VEHICLE_FILTER_DEFINITIONS.map((definition) => [definition.id, definition])
);

export function getFilterDefinition<Id extends VehicleFilterId>(
  id: Id
): VehicleFilterDefinitionById<Id> {
  const definition = filterDefinitionMap.get(id);
  if (!definition) {
    throw new Error(`Unknown filter definition for id "${id}"`);
  }
  return definition as VehicleFilterDefinitionById<Id>;
}

export function createDefaultFilterValues(): VehicleFilterValues {
  const entries = VEHICLE_FILTER_DEFINITIONS.map(
    (definition) => [definition.id, definition.defaultValue] as const
  );
  return Object.fromEntries(entries) as unknown as VehicleFilterValues;
}

function getSingleValue(params: URLSearchParams, key: string): string | null {
  const values = params.getAll(key);
  if (values.length === 0) {
    return null;
  }
  if (values.length === 1) {
    return values[0];
  }
  return values.join(",");
}

export function validateFilterQueryParams(
  params: URLSearchParams
): Array<{ param: string; message: string }> {
  const issues: Array<{ param: string; message: string }> = [];

  for (const definition of VEHICLE_FILTER_DEFINITIONS) {
    const raw = getSingleValue(params, definition.param);
    if (!definition.validateQuery(raw)) {
      issues.push({
        param: definition.param,
        message: `Ungültiger Wert für Parameter "${definition.param}"`,
      });
    }
  }

  return issues;
}

export function readFiltersFromSearchParams(
  params: URLSearchParams
): VehicleFilterValues {
  return Object.fromEntries(
    VEHICLE_FILTER_DEFINITIONS.map((definition) => [
      definition.id,
      definition.parse(params),
    ])
  ) as VehicleFilterValues;
}

export function applyFilterUpdates(
  params: URLSearchParams,
  updates: Partial<VehicleFilterValues>
): URLSearchParams {
  const nextParams = new URLSearchParams(params.toString());
  const currentValues = readFiltersFromSearchParams(nextParams);
  const nextValues = {
    ...currentValues,
    ...updates,
  } as VehicleFilterValues;

  for (const definition of VEHICLE_FILTER_DEFINITIONS) {
    definition.serialize(nextParams, nextValues[definition.id] as never);
  }

  return nextParams;
}

export function resetAllFilters(params: URLSearchParams): URLSearchParams {
  const nextParams = new URLSearchParams(params.toString());

  for (const definition of VEHICLE_FILTER_DEFINITIONS) {
    definition.serialize(nextParams, definition.defaultValue as never);
  }

  return nextParams;
}

export function normalizeFilterParams(params: URLSearchParams): string {
  const clone = new URLSearchParams(params.toString());
  clone.delete("page");
  return clone.toString();
}

export function areFilterParamsEqual(
  currentParams: URLSearchParams,
  nextParams: URLSearchParams
): boolean {
  return (
    normalizeFilterParams(currentParams) === normalizeFilterParams(nextParams)
  );
}

export function createFilterUpdate<Id extends VehicleFilterId>(
  id: Id,
  value: VehicleFilterValues[Id]
): Partial<VehicleFilterValues> {
  return { [id]: value } as Partial<VehicleFilterValues>;
}

export function formatActiveFilterChips(
  values: VehicleFilterValues,
  structure?: StructureT
): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];

  for (const definition of VEHICLE_FILTER_DEFINITIONS) {
    const value = values[definition.id];
    if (!definition.isActive(value as never)) {
      continue;
    }
    const label =
      (
        definition.formatChip as
          | ((v: never, ctx: { structure?: StructureT }) => string | null)
          | undefined
      )?.(value as never, { structure }) ?? definition.label;
    if (!label) {
      continue;
    }
    chips.push({
      id: definition.id,
      label,
      param: definition.param,
    });
  }

  return chips;
}

export function toVehicleFilterApiInput(
  values: VehicleFilterValues
): VehicleFilterApiInput | undefined {
  const payload: VehicleFilterApiInput = {};

  for (const definition of VEHICLE_FILTER_DEFINITIONS) {
    const apiValue = definition.toApi(values[definition.id] as never);
    if (apiValue !== undefined) {
      (payload as Record<string, unknown>)[definition.apiKey] = apiValue;
    }
  }

  return Object.keys(payload).length > 0 ? payload : undefined;
}

export function mergeFiltersWithDefaults(
  input?: VehicleFilterApiInput
): VehicleFilterValues {
  const defaults = createDefaultFilterValues();
  if (!input) {
    return defaults;
  }

  for (const definition of VEHICLE_FILTER_DEFINITIONS) {
    const key = definition.apiKey as keyof VehicleFilterApiInput;
    if (Object.prototype.hasOwnProperty.call(input, key)) {
      const value = input[key];
      if (value !== undefined) {
        (defaults as Record<string, unknown>)[definition.id] = value;
      }
    }
  }

  return defaults;
}

export function hasActiveVehicleFilters(values: VehicleFilterValues): boolean {
  return VEHICLE_FILTER_DEFINITIONS.some((definition) =>
    definition.isActive(values[definition.id] as never)
  );
}

function matchesAllFilters(
  vehicle: VehicleT,
  values: VehicleFilterValues,
  structure: StructureT,
  skipFilterId?: VehicleFilterId
): boolean {
  const makeId = getVehicleMakeId(vehicle);
  const makeLabel = makeId ? structure.make?.[makeId] : undefined;
  if (isExcludedMakeLabel(makeLabel)) {
    return false;
  }

  for (const definition of VEHICLE_FILTER_DEFINITIONS) {
    if (definition.id === skipFilterId) {
      continue;
    }
    const value = values[definition.id] as never;
    if (!definition.isActive(value as never)) {
      continue;
    }
    if (
      !definition.matches({ value: value as never, values, vehicle, structure })
    ) {
      return false;
    }
  }
  return true;
}

export function filterVehicles(
  vehicles: VehicleT[],
  values: VehicleFilterValues,
  structure: StructureT
): VehicleT[] {
  return vehicles.filter((vehicle) =>
    matchesAllFilters(vehicle, values, structure)
  );
}

function isExcludedVehicle(structure: StructureT, vehicle: VehicleT): boolean {
  const makeId = getVehicleMakeId(vehicle);
  const makeLabel = makeId ? structure.make?.[makeId] : undefined;
  return isExcludedMakeLabel(makeLabel);
}

export function computeVehicleFacets(
  filteredVehicles: VehicleT[],
  allVehicles: VehicleT[],
  values: VehicleFilterValues,
  structure: StructureT
): VehicleFilterFacets {
  const counts: VehicleFilterFacets["counts"] = {};

  for (const definition of VEHICLE_FILTER_DEFINITIONS) {
    const facet = (
      definition as {
        facet?: {
          getKeys: (
            vehicle: VehicleT,
            ctx: { structure: StructureT; values: VehicleFilterValues }
          ) => string[];
        };
      }
    ).facet;
    if (!facet) {
      continue;
    }

    const record: Record<string, number> = {};
    for (const vehicle of allVehicles) {
      if (isExcludedVehicle(structure, vehicle)) {
        continue;
      }
      if (!matchesAllFilters(vehicle, values, structure, definition.id)) {
        continue;
      }

      const keys = facet.getKeys(vehicle, { structure, values });
      for (const key of keys) {
        if (!key) continue;
        record[key] = (record[key] ?? 0) + 1;
      }
    }

    if (Object.keys(record).length === 0 && filteredVehicles.length > 0) {
      for (const vehicle of filteredVehicles) {
        if (isExcludedVehicle(structure, vehicle)) {
          continue;
        }
        const keys = facet.getKeys(vehicle, { structure, values });
        for (const key of keys) {
          if (!key) continue;
          record[key] = (record[key] ?? 0) + 1;
        }
      }
    }

    counts[definition.id] = record;
  }

  return {
    total: filteredVehicles.length,
    counts,
  } satisfies VehicleFilterFacets;
}

export function getFilterOptions(
  definition: VehicleFilterDefinition,
  context: FilterOptionContext
): ReadonlyArray<FilterOption<string>> {
  if (definition.getOptions) {
    return definition.getOptions(context) as ReadonlyArray<
      FilterOption<string>
    >;
  }

  const control = definition.control;

  if (control.kind === "checkbox") {
    const options = control.options;
    return options.map((opt) => ({ value: opt.value, label: opt.label }));
  }
  if (control.kind === "select" && Array.isArray(control.options)) {
    const options = control.options;
    return options.map((opt) => ({ value: opt.value, label: opt.label }));
  }

  return [];
}

export function createSearchParamsFromFilters(
  filters: VehicleFilterValues
): URLSearchParams {
  const params = new URLSearchParams();
  for (const definition of VEHICLE_FILTER_DEFINITIONS) {
    definition.serialize(params, filters[definition.id] as never);
  }
  return params;
}

export function appendApiFiltersToSearchParams(
  apiInput: VehicleFilterApiInput | undefined,
  params: URLSearchParams
): URLSearchParams {
  if (!apiInput) {
    return params;
  }

  for (const definition of VEHICLE_FILTER_DEFINITIONS) {
    const key = definition.apiKey as keyof VehicleFilterApiInput;
    const value = apiInput[key];
    if (value === undefined) {
      continue;
    }
    definition.serialize(params, value as never);
  }

  // Handle sort parameter separately since it's not in filter definitions
  if (apiInput.sort) {
    params.set("sort", apiInput.sort);
  }

  return params;
}

export type {
  VehicleFilterDefinition,
  VehicleFilterFacets,
  VehicleFilterId,
  VehicleFilterValues,
};
